import React, { useEffect, useState } from "react";
import {
  AppBar,
  Paper,
  Grid,
  Chip,
  Button,
  ButtonGroup,
  Backdrop,
  Snackbar,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { makeStyles } from "tss-react/mui";
import ResellerSummary from "./Finance/Finance";
import Legal from "./Legal/Legal";
import Profile from "./Profile/Profile";
import Approval from "./Approval/Approval";
import { NavLink, useHistory } from "react-router-dom";
import { AppConfig, FORM_STATUS, roles } from "../../../../configs/admin-config";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import { getAccessToken, checkIfRoleExists } from "../../../../utils/oauth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationDialog from "../../Dialog/ConfirmationDialog";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { pdf } from "@react-pdf/renderer";
import AdminPDFReport from "./AdminPDFReport";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && (
        <Box p={2}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  root: {
    paddingTop: "48px",
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  container: {
    flexGrow: 1,
    paddingBottom: 30,
    paddingTop: 30,
    paddingLeft: "5%",
    paddingRight: "5%",
  },
  formStatusGrid: {
    marginBottom: 20,
  },
  appbar: {
    backgroundColor: theme.palette.primary.main,
    alignItems: "center",
  },
  tabs: {
    textTransform: "none",
    color: "white",
    padding: "12px 50px",
    "&.Mui-selected": { color: "white" },
  },
  tabPanel: {
    backgroundColor: "white",
  },
  button: {
    backgroundColor: "white",
    textTransform: "none",
  },
  buttonGrid: {
    textAlign: "end",
  },
}));

const ResellerDashboard = (props) => {
  const history = useHistory();
  const { classes } = useStyles();
  const theme = useTheme();
  const [backdropOpen, setbackDrop] = useState(true);
  const [copySnack, setCopy] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [failSnack, setFail] = useState(false);
  const [value, setValue] = React.useState(0);
  const [formRender, setFormRender] = React.useState(0);
  const [companyData, setCompanyData] = React.useState({});
  const [approvalEmail, setApprovalEmail] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  const getResellerData = async () => {
    const requestURL = AppConfig.baseUrl + AppConfig.partnerData + props.match.params.id;
    try {
      let response = await fetch(requestURL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      });
      let body = await response.json();
      if (response.status === 200) {
        if (body && body.length > 0) {
          setCompanyData(body[0]);
        } else {
          console.error(body.message);
          history.push({
            pathname: AppConfig.urls.NOT_FOUND_PAGE,
          });
        }
      } else {
        console.error(body.message);
      }
      setbackDrop(false);
      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  const pushToNoAccessPage = () => {
    history.push({
      pathname: AppConfig.urls.NO_ACCESS_PAGE,
    });
  };

  const switchToTab = () => {
    if (props.match.params.tabName === "profile") {
      setValue(0);
    } else if (props.match.params.tabName === "finance") {
      if (checkIfRoleExists(roles.finance)) {
        setValue(1);
      } else {
        pushToNoAccessPage();
      }
    } else if (props.match.params.tabName === "legal") {
      if (
        checkIfRoleExists(roles.legal) ||
        checkIfRoleExists(roles.financeCreator) ||
        checkIfRoleExists(roles.financeReviewer) ||
        checkIfRoleExists(roles.financeApprover)
      ) {
        setValue(2);
      } else {
        pushToNoAccessPage();
      }
    } else if (props.match.params.tabName === "approvalsummary") {
      setValue(3);
    } else {
      pushToNoAccessPage();
    }
  };

  useEffect(() => {
    getResellerData();
    switchToTab();
  }, [formRender]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const openProfileTab = () => {
    history.replace({
      pathname:
        AppConfig.urls.ADMIN +
        AppConfig.urls.RESELLER_DASHBOARD +
        props.match.params.id +
        "/" +
        (AppConfig.resellerDashboardTabs[0] ? AppConfig.resellerDashboardTabs[0] : ""),
    });
  };

  const openFinanceTab = () => {
    history.replace({
      pathname:
        AppConfig.urls.ADMIN +
        AppConfig.urls.RESELLER_DASHBOARD +
        props.match.params.id +
        "/" +
        (AppConfig.resellerDashboardTabs[1] ? AppConfig.resellerDashboardTabs[1] : ""),
    });
  };

  const openLegalTab = () => {
    history.replace({
      pathname:
        AppConfig.urls.ADMIN +
        AppConfig.urls.RESELLER_DASHBOARD +
        props.match.params.id +
        "/" +
        (AppConfig.resellerDashboardTabs[2] ? AppConfig.resellerDashboardTabs[2] : ""),
    });
  };

  const openApprovalSummaryTab = () => {
    history.replace({
      pathname:
        AppConfig.urls.ADMIN +
        AppConfig.urls.RESELLER_DASHBOARD +
        props.match.params.id +
        "/" +
        (AppConfig.resellerDashboardTabs[3] ? AppConfig.resellerDashboardTabs[3] : ""),
    });
  };

  const allowEditAccess = (partner, status) => async (e) => {
    setbackDrop(true);
    const requestURL = AppConfig.baseUrl + AppConfig.partnerData + partner.companyId + "/" + AppConfig.formStatus;
    let data = {
      formStatus: status,
      clientUrl: AppConfig.externalUrl,
    };
    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(data),
      });
      let body = await response.json();
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        setFormRender(formRender + 1);
      } else {
        console.error(body.message);
        setFail(true);
      }
      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

    useEffect(() => {
    if (companyData && companyData.companyId) {
      // Use the sentForApproval field from database
      if (companyData.sentForApproval && companyData.sentForApproval === 1 ) {
        setApprovalEmail(true);
      } else {
        setApprovalEmail(false);
      }
    }
  }, [companyData]);

  const sendApprovalEmail = async () => {
    setbackDrop(true);
    const requestURL = AppConfig.baseUrl + AppConfig.sendApprovalEmail;
    let data = {
      id: companyData.companyId,
      name: companyData.companyName?? "",
      sentForApproval: 1
    };
    try {
      let response = await fetch(requestURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(data),
      });
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        setApprovalEmail(true);
      } else {
        throw new Error("Failed to send email");
      }
      return null;
    } catch (error) {
      setFail(true);
      return error;
    }
  }

  const copyURL = (e) => {
    e.stopPropagation();
    const externalUrl = AppConfig.externalUrl;
    const linkURL = externalUrl + "partners/validate/" + props.location.state.encodeString;
    let el = document.createElement("textarea");
    el.value = linkURL;
    el.setAttribute("readonly", "");
    el.style = { position: "absolute", left: "-9999px" };
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    setCopy(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setFail(false);
    setCopy(false);
  };

  const handleConfirmationDialog = (content) => {
    setConfirmationDialog(content);
  };

  const handleTradeReference = (value) => {
    handleConfirmationDialog({
      title: "You are about to make a change to a reseller!",
      text: "Are you sure you want to " + (value ? "enable" : "disable") + " Trade Reference for this reseller?",
      confirmAction: () => {
        changeValue(value);
      },
    });
  };

  const changeValue = async (value) => {
    setbackDrop(true);
    const isTradeReferenceEnabled = value
    const requestURL =
      AppConfig.baseUrl +
      AppConfig.partnerData +
      companyData.linkId;

    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify({ isTradeReferenceEnabled }),
      });
      let body = await response.json();
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        setCompanyData((prev) => ({ ...prev, isTradeReferenceEnabled: value }));
      } else {
        console.error(body.message);
        setFail(true);
      }
      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  const generateReport = async (company) => {
    setbackDrop(true);
    const companyId = company.companyId;
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + getAccessToken(),
    };

    try {
      const [
        financeAnswers,
        financeQuestions,
        creditScoreRes,
        legalAns,
        legalRes,
        approvalRes,
      ] = await Promise.all([
        fetch(
          AppConfig.baseUrl +
            AppConfig.partnerData +
            companyId +
            "/" +
            AppConfig.answers,
          { method: "GET", headers },
        ),
        fetch(AppConfig.baseUrl + AppConfig.partnerQuestions, {
          method: "GET",
          headers,
        }),
        fetch(AppConfig.baseUrl + AppConfig.creditScoreItems + companyId, {
          method: "GET",
          headers,
        }),
        fetch(
          AppConfig.baseUrl +
            AppConfig.partnerData +
            companyId +
            "/" +
            AppConfig.legalAnswers,
          { method: "GET", headers },
        ),
        fetch(
          AppConfig.baseUrl +
            AppConfig.partnerData +
            companyId +
            "/" +
            AppConfig.legalQuestions,
          { method: "GET", headers },
        ),
        fetch(
          AppConfig.baseUrl +
            AppConfig.partnerData +
            companyId +
            "/" +
            AppConfig.approvalSummary,
          { method: "GET", headers },
        ),
      ]);

      for (const res of [financeAnswers, financeQuestions, creditScoreRes, legalAns, legalRes, approvalRes]) {
        if (!res.ok) throw new Error(`API error ${res.status} from ${res.url}`);
      }

      const [
        financeAnswersJson,
        financeQuestionsJson,
        creditScoreJson,
        legalAnswersJson,
        legalQuestionsJson,
        approvalJson,
      ] = await Promise.all([
        financeAnswers.json(),
        financeQuestions.json(),
        creditScoreRes.json(),
        legalAns.json(),
        legalRes.json(),
        approvalRes.json(),
      ]);

      const creditScoreData = {
        year1: creditScoreJson.items?.find((i) => i.year === 1) || {},
        year2: creditScoreJson.items?.find((i) => i.year === 2) || {},
        year3: creditScoreJson.items?.find((i) => i.year === 3) || {},
        ratios: creditScoreJson.ratios || [],
        currency: creditScoreJson.currency?.[0]?.descriptionAnswer || "",
      };

      const approvalData = {
        financeResult: approvalJson.summary?.[0]?.financeResult || "",
        financeSpecialApproval: approvalJson.summary?.[0]?.financeSpecialApproval || "",
        legalResult: approvalJson.summary?.[0]?.legalResult || "",
        resellerStatus: approvalJson.resellerLinks?.[0]?.status || "",
      };

      const reportDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const blob = await pdf(
        <AdminPDFReport
          companyData={company}
          financeCategories={financeQuestionsJson.questionCategoryInfo || []}
          financeQuestions={financeQuestionsJson.questionInfo || []}
          financeSubQuestions={financeQuestionsJson.subQuestionInfo || []}
          financeAnswers={financeAnswersJson.answers || []}
          financeComments={financeAnswersJson.comments || []}
          creditScoreData={creditScoreData}
          legalQuestions={legalQuestionsJson.questions || []}
          legalSubQuestions={legalQuestionsJson.subQuestions || []}
          legalAnswers={legalAnswersJson.answers || []}
          legalComments={legalAnswersJson.comments || []}
          approvalData={approvalData}
          reportDate={reportDate}
        />
      ).toBlob();

      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const fileName = `${(company.companyName || "Report").replace(/\s+/g, "_")}_Due_Diligence_Report_${dateStr}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setbackDrop(false);
    } catch (error) {
      console.error(error);
      setFail(true);
      setbackDrop(false);
    }
  };

  const isFullEditingAllowed =
    companyData.formStatus === FORM_STATUS.DRAFTED ||
    companyData.formStatus === FORM_STATUS.ACTIVE ||
    companyData.formStatus === FORM_STATUS.FULL_EDIT_REQUESTED;

  const isTrEditingAllowed =
    companyData.formStatus === FORM_STATUS.TR_EDIT_REQUESTED || companyData.formStatus === FORM_STATUS.TR_DRAFTED;

  return (
    <div className={classes.root}>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>

      <Snackbar
        open={successSnack}
        autoHideDuration={3000}
        onClose={handleClose}
       
      >
        <Alert onClose={handleClose} severity="success">
          Success!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={copySnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Copied to Clipboard!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={failSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Failed!
        </Alert>
      </Snackbar>

      <div className={classes.container}>
        <Grid container spacing={1} className={classes.formStatusGrid}>
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              justifycontent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <NavLink
                  to={AppConfig.urls.ADMIN + AppConfig.urls.LANDING_PAGE}
                >
                  <IconButton aria-label="back">
                    <ArrowBackIcon />
                  </IconButton>
                </NavLink>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} lg={8} style={{ textAlign: "justify" }}>
            <Chip
              variant="outlined"
              label={
                companyData.formStatus === FORM_STATUS.SIGNED
                  ? "Form Status : Completed"
                  : companyData.formStatus === FORM_STATUS.COMPLETED ||
                      companyData.formStatus === FORM_STATUS.EDIT_COMPLETED
                    ? "Form Status : Pending Signature"
                    : companyData.formStatus ===
                          FORM_STATUS.TR_EDIT_REQUESTED ||
                        companyData.formStatus ===
                          FORM_STATUS.FULL_EDIT_REQUESTED
                      ? "Form Status : Requested to Edit"
                      : companyData.formStatus === FORM_STATUS.DRAFTED ||
                          companyData.formStatus === FORM_STATUS.TR_DRAFTED
                        ? "Form Status : Drafted"
                        : "Form Status : Pending"
              }
              color={"primary"}
            />
          </Grid>
          {/* <Grid item xs={12} md={3} lg={4} className={classes.buttonGrid}>
              {props.location.state && (checkIfRoleExists(roles.admin) || checkIfRoleExists(roles.channelManager)) 
                && props.location.state.encodeString!=="" && (
                <Button 
                  className={classes.button} 
                  variant="outlined" 
                  color="primary" 
                  onClick={copyURL} 
                  startIcon={<LinkOutlinedIcon />}>Generate Form URL</Button>
              )}
          </Grid> */}
          {checkIfRoleExists(roles.admin) && (
            <Grid item xs={12} md={6} lg={12} className={classes.buttonGrid}>
              <ButtonGroup
                variant="outlined"
                color="primary"
                aria-label="outlined primary button group"
              >
                <Button
                  className={classes.button}
                  startIcon={<NoteAddIcon />}
                  onClick={() => generateReport(companyData)}
                >
                  Generate Report
                </Button>
                <Button
                  className={classes.button}
                  startIcon={isTrEditingAllowed ? <DoneIcon /> : <EditIcon />}
                  disabled={isTrEditingAllowed}
                  onClick={allowEditAccess(companyData, "tr_edit_requested")}
                >
                  {isTrEditingAllowed
                    ? "Only TR Editing Allowed"
                    : "Allow Only TR Editing"}
                </Button>
                <Button
                  className={classes.button}
                  startIcon={isFullEditingAllowed ? <DoneIcon /> : <EditIcon />}
                  disabled={isFullEditingAllowed}
                  onClick={allowEditAccess(companyData, "full_edit_requested")}
                >
                  {isFullEditingAllowed
                    ? "Full Editing Allowed"
                    : "Allow Full Editing"}
                </Button>
                {(checkIfRoleExists(roles.finance) || checkIfRoleExists(roles.legal) || checkIfRoleExists(roles.admin)) &&
                  companyData.formStatus !== FORM_STATUS.SIGNED &&
                  (companyData.isTradeReferenceEnabled ? (
                    <Button
                      className={classes.button}
                      variant="outlined"
                      color="primary"
                      onClick={() => handleTradeReference(false)}
                    >
                      Disable TR
                    </Button>
                  ) : (
                    <Button
                      className={classes.button}
                      variant="outlined"
                      color="primary"
                      onClick={() => handleTradeReference(true)}
                    >
                      Enable TR
                    </Button>
                  ))}
              </ButtonGroup>
            </Grid>
          )}
        </Grid>

        <Paper>
          <AppBar position="static" className={classes.appbar}>
            <Tabs value={value} onChange={handleChange} scrollButtons="auto" textColor="inherit" TabIndicatorProps={{ style: { backgroundColor: theme.palette.secondary.main, height: 3 } }}>
              <Tab
                className={classes.tabs}
                companyData={companyData}
                label="Profile"
                onClick={openProfileTab}
              />
              <Tab
                className={classes.tabs}
                label="Finance"
                onClick={openFinanceTab}
                disabled={!checkIfRoleExists(roles.finance)}
              />
              <Tab
                className={classes.tabs}
                label="Legal"
                onClick={openLegalTab}
                disabled={
                  !(
                    checkIfRoleExists(roles.legal) ||
                    checkIfRoleExists(roles.financeCreator) ||
                    checkIfRoleExists(roles.financeReviewer) ||
                    checkIfRoleExists(roles.financeApprover)
                  )
                }
              />
              <Tab
                className={classes.tabs}
                label="Approval"
                onClick={openApprovalSummaryTab}
              />
            </Tabs>
          </AppBar>
          <TabPanel className={classes.tabPanel} value={value} index={0}>
            <Profile id={props.match.params.id} />
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={value} index={1}>
            <ResellerSummary
              companyId={props.match.params.id}
              approvalEmailSent={approvalEmail}
              sendApprovalEmail={sendApprovalEmail}
              companyData={companyData}
            />
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={value} index={2}>
            <Legal
              companyId={props.match.params.id}
              companyData={companyData}
            />
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={value} index={3}>
            <Approval companyId={props.match.params.id} />
          </TabPanel>
        </Paper>
      </div>
      {confirmationDialog && (
        <ConfirmationDialog
          content={confirmationDialog}
          handleOpen={handleConfirmationDialog}
        />
      )}
    </div>
  );
};

export default ResellerDashboard;
